import {Box, Chip, Divider, Link, Paper, Stack, Typography} from '@mui/material';
import {WarningAmber, InfoOutlined} from '@mui/icons-material';
import {Trans} from '@lingui/react/macro';
import {t} from '@lingui/core/macro';
import {useGetProviderAttentionQuery} from '../../../apis/Provider.api.ts';
import {AttentionItem, AttentionKind} from '../../../types/models/Attention';
import {fixedFormat} from '../../../../utils/numberFormat.ts';
import {displayDate} from '../../../../utils/formatDate.ts';
import ProviderAttentionLink from './ProviderAttentionLink';
import React from 'react';

type ProviderAttentionListProps = {
    providerId?: string,
};

/** How many items of a group are shown before the rest is folded away. */
const ITEMS_PER_GROUP = 5;

const KIND_ORDER: AttentionKind[] = ['stalledCutting', 'agingStock', 'stuckShipment'];

/**
 * The heading of a group of items.
 *
 * The translation is asked for here and not at the top of the file: a
 * translation that runs while the module is loaded would run before Lingui
 * has been told which language to use.
 */
const kindLabel = (kind: AttentionKind) => {
    switch (kind) {
        case 'stalledCutting':
            return t`Cuttings that have gone quiet`;
        case 'agingStock':
            return t`Timber standing in the storages`;
        default:
            return t`Shipments that should have gone out`;
    }
};

const AttentionRow = ({item}: {item: AttentionItem}) => (
    <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start', padding: '0.5em 0'}}>
        {item.severity === 'warning'
            ? <WarningAmber color="warning" fontSize="small" sx={{marginTop: '0.2em'}}/>
            : <InfoOutlined color="info" fontSize="small" sx={{marginTop: '0.2em'}}/>
        }
        <Box>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                <ProviderAttentionLink item={item}>
                    {item.name}
                </ProviderAttentionLink>
                {item.overdue && item.kind === 'stalledCutting' &&
                <Chip size="small" color="warning" label={<Trans>Overdue</Trans>}/>
                }
            </Stack>
            <Typography variant="body2" color="text.secondary">
                {item.kind === 'stalledCutting' && (
                    item.overdue
                        ? <Trans>{`No timber has gone in for ${item.ageDays} days and the cutting was due before that.`}</Trans>
                        : <Trans>{`No timber has gone in for ${item.ageDays} days.`}</Trans>
                )}
                {item.kind === 'agingStock' && (
                    <Trans>
                        {`${fixedFormat(item.volume ?? 0)} m³ in ${item.count ?? 0} pieces has been standing since ${displayDate(new Date(item.since ?? '')) ?? ''}.`}
                    </Trans>
                )}
                {item.kind === 'stuckShipment' && (
                    <Trans>{`The shipment was due ${item.ageDays} days ago.`}</Trans>
                )}
            </Typography>
        </Box>
    </Stack>
);

/**
 * What is going on with the provider that should not be going on.
 *
 * The list is left out entirely when there is nothing to say: a panel that
 * always shows up and is always empty is noise.
 */
const ProviderAttentionList = ({providerId}: ProviderAttentionListProps) => {

    const [showAll, setShowAll] = React.useState(false);

    const {data, isFetching} = useGetProviderAttentionQuery(
        {providerId: providerId!, request: {}},
        {skip: !providerId}
    );

    if (isFetching && !data) {
        return null;
    }

    if (!data || data.items.length === 0) {
        return null;
    }

    const grouped = KIND_ORDER
        .map(kind => ({kind, items: data.items.filter(item => item.kind === kind)}))
        .filter(group => group.items.length > 0);

    const hidden = showAll ? 0 : grouped.reduce((acc, group) => acc + Math.max(0, group.items.length - ITEMS_PER_GROUP), 0);

    return (
        <Paper sx={{padding: '1em'}} data-testid="Provider-attention">
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', marginBottom: '0.5em'}}>
                <Typography variant="h6" data-testid="Provider-attention-title"><Trans>Needs attention</Trans></Typography>
                <Chip size="small" color="warning" label={data.total}/>
                {data.truncated &&
                <Typography variant="caption" color="text.secondary">
                    <Trans>{`showing the ${data.items.length} worst of ${data.total}`}</Trans>
                </Typography>
                }
            </Stack>

            {grouped.map((group, index) => (
                <Box key={group.kind}>
                    {index > 0 && <Divider sx={{margin: '0.5em 0'}}/>}
                    <Typography variant="subtitle2" color="text.secondary">
                        {kindLabel(group.kind)}
                    </Typography>
                    {group.items.slice(0, showAll ? group.items.length : ITEMS_PER_GROUP).map(item => (
                        <AttentionRow key={`${item.kind}-${item.entityId}`} item={item}/>
                    ))}
                    {group.items.length > ITEMS_PER_GROUP && !showAll &&
                    <Typography variant="caption" color="text.secondary">
                        <Trans>{`and ${group.items.length - ITEMS_PER_GROUP} more`}</Trans>
                    </Typography>
                    }
                </Box>
            ))}

            {hidden > 0 &&
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', marginTop: '0.5em'}}>
                <Link href="#" underline="hover" onClick={(event) => {
                    event.preventDefault();
                    setShowAll(true);
                }}>
                    <Trans>{`Show all ${data.total}`}</Trans>
                </Link>
            </Stack>
            }
        </Paper>
    );
};

export default ProviderAttentionList;
